import React from 'react';
import { EVMData } from '../types/EVM';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { formatCurrency } from '../utils/evmCalculations';

interface EVMChartProps {
  data: EVMData[];
  width?: number;
  height?: number;
}

export const EVMChart: React.FC<EVMChartProps> = ({ 
  data, 
  width = 800, 
  height = 400 
}) => {
  if (data.length === 0) {
    return (
      <div className="evm-chart-empty">
        <p>EVMデータがありません</p>
      </div>
    );
  }

  // データの最大値と最小値を計算
  const maxValue = Math.max(
    ...data.flatMap(d => [d.plannedValue, d.earnedValue, d.actualCost])
  );
  const minValue = Math.min(
    ...data.flatMap(d => [d.plannedValue, d.earnedValue, d.actualCost])
  );
  
  const chartMargin = { top: 20, right: 100, bottom: 60, left: 80 };
  const chartWidth = width - chartMargin.left - chartMargin.right;
  const chartHeight = height - chartMargin.top - chartMargin.bottom;
  
  // スケール関数
  const xScale = (index: number) => (index / (data.length - 1)) * chartWidth;
  const yScale = (value: number) => 
    chartHeight - ((value - minValue) / (maxValue - minValue)) * chartHeight;
  
  // パスデータの生成
  const createPath = (values: number[]) => {
    return values
      .map((value, index) => {
        const x = xScale(index);
        const y = yScale(value);
        return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
      })
      .join(' ');
  };
  
  const pvPath = createPath(data.map(d => d.plannedValue));
  const evPath = createPath(data.map(d => d.earnedValue));
  const acPath = createPath(data.map(d => d.actualCost));
  
  // Y軸のティック値
  const yTicks = Array.from({ length: 6 }, (_, i) => 
    minValue + (maxValue - minValue) * (i / 5)
  );
  
  // X軸のティック（日付）
  const xTicks = data.filter((_, index) => index % Math.ceil(data.length / 6) === 0);

  return (
    <div className="evm-chart-container">
      <h3>EVMトレンドチャート</h3>
      <svg width={width} height={height} className="evm-chart">
        {/* 背景グリッド */}
        <defs>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#f0f0f0" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect 
          x={chartMargin.left} 
          y={chartMargin.top}
          width={chartWidth} 
          height={chartHeight} 
          fill="url(#grid)"
        />
        
        {/* Y軸 */}
        <line
          x1={chartMargin.left}
          y1={chartMargin.top}
          x2={chartMargin.left}
          y2={chartMargin.top + chartHeight}
          stroke="#666"
          strokeWidth="1"
        />
        
        {/* Y軸ラベル */}
        {yTicks.map((value, index) => (
          <g key={index}>
            <line
              x1={chartMargin.left - 5}
              y1={chartMargin.top + yScale(value)}
              x2={chartMargin.left}
              y2={chartMargin.top + yScale(value)}
              stroke="#666"
            />
            <text
              x={chartMargin.left - 10}
              y={chartMargin.top + yScale(value) + 4}
              textAnchor="end"
              fontSize="12"
              fill="#666"
            >
              {formatCurrency(value)}
            </text>
          </g>
        ))}
        
        {/* X軸 */}
        <line
          x1={chartMargin.left}
          y1={chartMargin.top + chartHeight}
          x2={chartMargin.left + chartWidth}
          y2={chartMargin.top + chartHeight}
          stroke="#666"
          strokeWidth="1"
        />
        
        {/* X軸ラベル */}
        {xTicks.map((dataPoint, index) => {
          const xPos = chartMargin.left + xScale(data.indexOf(dataPoint));
          return (
            <g key={index}>
              <line
                x1={xPos}
                y1={chartMargin.top + chartHeight}
                x2={xPos}
                y2={chartMargin.top + chartHeight + 5}
                stroke="#666"
              />
              <text
                x={xPos}
                y={chartMargin.top + chartHeight + 20}
                textAnchor="middle"
                fontSize="10"
                fill="#666"
              >
                {format(dataPoint.date, 'MM/dd', { locale: ja })}
              </text>
            </g>
          );
        })}
        
        {/* データライン */}
        <g transform={`translate(${chartMargin.left}, ${chartMargin.top})`}>
          {/* PV (計画価値) - 青線 */}
          <path
            d={pvPath}
            fill="none"
            stroke="#2563eb"
            strokeWidth="2"
            strokeDasharray="5,5"
          />
          
          {/* EV (出来高価値) - 緑線 */}
          <path
            d={evPath}
            fill="none"
            stroke="#10b981"
            strokeWidth="3"
          />
          
          {/* AC (実績コスト) - 赤線 */}
          <path
            d={acPath}
            fill="none"
            stroke="#ef4444"
            strokeWidth="2"
          />
          
          {/* データポイント */}
          {data.map((dataPoint, index) => (
            <g key={index}>
              {/* PV ポイント */}
              <circle
                cx={xScale(index)}
                cy={yScale(dataPoint.plannedValue)}
                r="3"
                fill="#2563eb"
                className="chart-point"
              />
              {/* EV ポイント */}
              <circle
                cx={xScale(index)}
                cy={yScale(dataPoint.earnedValue)}
                r="4"
                fill="#10b981"
                className="chart-point"
              />
              {/* AC ポイント */}
              <circle
                cx={xScale(index)}
                cy={yScale(dataPoint.actualCost)}
                r="3"
                fill="#ef4444"
                className="chart-point"
              />
            </g>
          ))}
        </g>
        
        {/* 凡例 */}
        <g transform={`translate(${width - 90}, ${chartMargin.top + 20})`}>
          <rect x="0" y="0" width="80" height="60" fill="white" stroke="#ddd" rx="4"/>
          
          <line x1="10" y1="15" x2="25" y2="15" stroke="#2563eb" strokeWidth="2" strokeDasharray="3,3"/>
          <text x="30" y="19" fontSize="11" fill="#666">PV (計画)</text>
          
          <line x1="10" y1="30" x2="25" y2="30" stroke="#10b981" strokeWidth="3"/>
          <text x="30" y="34" fontSize="11" fill="#666">EV (出来高)</text>
          
          <line x1="10" y1="45" x2="25" y2="45" stroke="#ef4444" strokeWidth="2"/>
          <text x="30" y="49" fontSize="11" fill="#666">AC (実績)</text>
        </g>
      </svg>
    </div>
  );
};
